import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header";
import { verifyKnowlarityApp, getKnowlarityCredentials, KnowlarityCredential } from "../../../api/knowlarity"; // Adjust the import path as needed
import { toast } from "react-toastify";

const CallPage = () => {
  const [credentials, setCredentials] = useState<KnowlarityCredential | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch Knowlarity credentials on component mount
  useEffect(() => {
    const fetchCredentials = async () => {
      try {
        const data = await getKnowlarityCredentials();
        setCredentials(data);
      } catch (error) {
        // Error is handled in the API function with toast
      } finally {
        setLoading(false);
      }
    };
    fetchCredentials();
  }, []);

  // Handle form submission for verifying credentials
  const handleVerifyCredentials = async (event: React.FormEvent<HTMLFormElement>, providerId: string) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const api_key = formData.get("api_key") as string;
    const auth_key = formData.get("auth_key") as string;
    const virtual_number = formData.get("virtual_number") as string;

    try {
      await verifyKnowlarityApp(api_key, auth_key, virtual_number);
      // Refresh credentials after successful verification
      const updatedCredentials = await getKnowlarityCredentials();
      setCredentials(updatedCredentials);
    } catch (error) {
      // Error is handled in the API function with toast
    }
  };

  const callProviders = [
    {
      id: "knowlarity",
      name: "Knowlarity",
      icon: "knowlarity.svg", // Ensure this icon exists in assets/img/icons/
      status: credentials?.is_active ? "Connected" : "Not Connected",
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Call Settings</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="row p-2 m-2">
                  <h4>Call Providers</h4>
                  {loading ? (
                    <p>Loading...</p>
                  ) : (
                    callProviders.map((provider) => (
                      <div className="col-md-6 mt-2" key={provider.id}>
                        <div className="border rounded p-3 mb-4">
                          <div className="row gy-3">
                            <div className="col-sm-5">
                              <div className="d-flex align-items-center">
                                <span className="avatar avtar-lg border me-2 flex-shrink-0">
                                  <ImageWithBasePath
                                    src={`assets/img/icons/${provider.icon}`}
                                    className="w-auto h-auto"
                                    alt=""
                                  />
                                </span>
                                <div>
                                  <h6 className="fw-medium mb-1">{provider.name}</h6>
                                  <Link
                                    to="#"
                                    className={`badge bg-soft-${provider.status === "Connected" ? "success" : "danger"}`}
                                  >
                                    {provider.status}
                                  </Link>
                                </div>
                              </div>
                            </div>
                            <div className="col-sm-7">
                              <div className="d-flex align-items-center justify-content-between">
                                <div>
                                  <Link to="#" className="me-3 text-default">
                                    <i className="ti ti-info-circle-filled me-1" />
                                  </Link>
                                  <Link
                                    to="#"
                                    className="btn btn-light"
                                    data-bs-toggle="modal"
                                    data-bs-target={`#${provider.id}_modal`}
                                  >
                                    <i className="ti ti-tool me-1" /> View Integration
                                  </Link>
                                </div>
                                <div className="status-toggle">
                                  <input
                                    type="checkbox"
                                    id={provider.id}
                                    className="check"
                                    checked={provider.status === "Connected"}
                                    readOnly
                                  />
                                  <label htmlFor={provider.id} className="checktoggle" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Knowlarity Settings */}
      {callProviders.map((provider) => (
        <div className="modal fade" id={`${provider.id}_modal`} role="dialog" key={provider.id}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{provider.name} Settings</h5>
                <button
                  className="btn-close custom-btn-close border p-1 me-0 text-dark"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <form onSubmit={(e) => handleVerifyCredentials(e, provider.id)}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="col-form-label">
                      API Key <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="api_key"
                      className="form-control"
                      defaultValue={credentials?.api_key || ""}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="col-form-label">
                      Auth Key <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="auth_key"
                      className="form-control"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="col-form-label">
                      Virtual Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="virtual_number"
                      className="form-control"
                      defaultValue={credentials?.virtual_number || ""}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <Link to="#" className="btn btn-light me-2" data-bs-dismiss="modal">
                    Cancel
                  </Link>
                  <button type="submit" className="btn btn-primary">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CallPage;